import {Component, OnInit, signal} from '@angular/core';
import {ControlsOf, FormControl, FormGroup} from '@ngneat/reactive-forms';
import {Leave, LeaveType} from '../../classes/leave';
import {CodaltComponent} from '../../codalt.component';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {ReactiveFormsModule, Validators} from '@angular/forms';
import {LeaveService} from '../../services/leave.service';
import {Settings} from '../../settings.class';
import {Utils} from '../../utils.class';
import {UserService} from '../../services/user/user.service';
import {User} from '../../classes/user.class';
import {DatePipe, formatDate} from '@angular/common';
import {debounceTime, startWith} from 'rxjs/operators';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {FieldErrorComponent} from '../../field-error/field-error.component';
import {MatCheckbox} from '@angular/material/checkbox';
import {NgNotFoundTemplateDirective, NgOptionTemplateDirective, NgSelectComponent} from '@ng-select/ng-select';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatTooltip} from '@angular/material/tooltip';
import {MinutesPipe} from '../../pipes/minutes.pipe';
import {DecimalInputDirective} from '../../directives/decimal-input.directive';
import {DatetimeInputComponent} from '../../services/datetime-input/datetime-input.component';
import {Registration} from '../../classes/registration';
import {BreadcrumbComponent} from '../../services/breadcrumb/breadcrumb.component';
import {Routenames} from '../../classes/route-name.enum';
import {AuthorisationService} from '../../services/auth/authorisation.service';
import {Roles} from '../../services/auth/roles';
import {HasRolePipe} from '../../pipes/has-role.pipe';
import {Title} from '@angular/platform-browser';
import {DatetimeBeforePipe} from '../../pipes/datetime-before.pipe';
import {ByIdPipe} from '../../by-id.pipe';

@Component({
    selector: 'app-leave-edit',
    templateUrl: './leave-edit.component.html',
    styleUrls: ['./leave-edit.component.scss'],
    imports: [
        MatFormField,
        ReactiveFormsModule,
        FieldErrorComponent,
        MatCheckbox,
        NgSelectComponent,
        NgNotFoundTemplateDirective,
        MatDatepickerToggle,
        MatDatepicker,
        MatDatepickerInput,
        MatInput,
        MatTooltip,
        MinutesPipe,
        DecimalInputDirective,
        DatePipe,
        RouterLink,
        DatetimeInputComponent,
        BreadcrumbComponent,
        ByIdPipe,
        HasRolePipe,
        NgOptionTemplateDirective,
        MatLabel,
    ]
})
export class LeaveEditComponent extends CodaltComponent implements OnInit {
    readonly form = signal<FormGroup<ControlsOf<FormGroupLeave>>>(null);
    readonly leave = signal<Leave>(null);
    readonly leaveTypes = signal<LeaveType[]>(null);
    readonly previewRealisations = signal<Registration[]>(null);
    readonly users = signal<User[]>(null);
    readonly registrations = signal<Registration[]>(null);
    readonly today = Utils.setTime(new Date(), 0, 0);

    minDate: Date;
    maxDate: Date;
    saving = false;
    saved = false;

    duration = 0;
    highlight = null;

    now = new Date();
    canSaveLeave: boolean = true;

    constructor(
        private activatedRoute: ActivatedRoute,
        private leaveService: LeaveService,
        private router: Router,
        private titleService: Title,
        private userService: UserService,
    ) {
        super();
        this.titleService.setTitle(`Verlof ${this.environment.titleAppend}`);
    }

    ngOnInit(): void {
        if (AuthorisationService.hasRole(Roles.Management)) {
            this.subscriptions.add(
                this.userService.getList().subscribe(users => {
                    this.users.set(users.filter(user => {
                        const now = new Date();
                        return user.employment_end === null || new Date(user.employment_end) < now;
                    }));
                })
            );
        }

        this.subscriptions.add(
            this.leaveService.getLeaveTypes().subscribe(leaveTypes => {
                if (AuthorisationService.hasRole(Roles.Management)) {
                    this.leaveTypes.set(leaveTypes);
                } else {
                    this.leaveTypes.set(leaveTypes.filter(leaveType => leaveType.allowed_user));
                }
            })
        );

        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { id }) => {
                this.crumbs = [
                    {name: 'Verlof', route: Routenames.leave},
                    {name: `Verlof : ${params.id}`, route: `${Routenames.leave}/${params.id}`}
                ];
                if (params.id === 'new') {
                    const leave = new Leave();
                    leave.users = [this.LocalStorage.user];
                    this.createForm(leave);
                } else {
                    this.subscriptions.add(
                        this.leaveService.getLeave(params.id).subscribe(leave => {
                            this.createForm(leave);
                        })
                    );
                }
            })
        );
    }

    editHours(date: Date, userId: number) {
        this.router.navigate([
            `/hours/${formatDate(date, 'yyyy-MM-dd', 'nl')}/${userId}`,
            {backTo: window.location.pathname}
        ]);
    }

    createForm(leave: Leave) {
        this.leave.set(leave);

        const beginDate = new Date();
        Utils.setTime(beginDate, Settings.DEFAULT_START, 0, 0);
        const endDate = new Date(beginDate);
        Utils.setTime(endDate, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);

        if (!leave.id || !Utils.isPast(leave.begindate)) {
            this.minDate = new Date(beginDate);
            this.minDate.setDate(this.minDate.getDate() - 100);
            this.minDate = new Date(Math.max(this.today.getTime(), this.minDate.getTime()));
        } else {
            this.minDate = new Date(leave.begindate);
        }

        const begintime = new Date();
        if (!leave.begintime) {
            Utils.setTime(begintime, Settings.DEFAULT_START, 0, 0);
        } else {
            const time = leave.begintime.split(':');
            Utils.setTime(begintime, +time[0], +time[1], 0);
        }
        const endtime = new Date();
        if (!leave.endtime) {
            Utils.setTime(endtime, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);
        } else {
            const time = leave.endtime.split(':');
            Utils.setTime(endtime, +time[0], +time[1], 0);
        }

        this.form.set(new FormGroup<ControlsOf<FormGroupLeave>>({
                id: new FormControl(leave.id),
                leave_type: new FormControl(leave.leave_type, Validators.required),
                comment: new FormControl(leave.comment),
                public_comment: new FormControl(leave.public_comment),
                user_id: new FormControl(leave.user_id),
                users: new FormControl(leave.users?.map(u => u.id)),
                common: new FormControl(leave.common ?? false),
                begindate: new FormControl(leave.begindate ? new Date(leave.begindate) : beginDate, Validators.required),
                enddate: new FormControl(leave.enddate ? new Date(leave.enddate) : endDate, Validators.required),
                begintime: new FormControl(begintime),
                endtime: new FormControl(endtime),
                interval_week: new FormControl(leave.interval_week, Validators.max(52)),
                repeat_monday: new FormControl(leave.repeat_monday),
                repeat_tuesday: new FormControl(leave.repeat_tuesday),
                repeat_wednesday: new FormControl(leave.repeat_wednesday),
                repeat_thursday: new FormControl(leave.repeat_thursday),
                repeat_friday: new FormControl(leave.repeat_friday),
                repeat_saturday: new FormControl(leave.repeat_saturday),
                repeat_sunday: new FormControl(leave.repeat_sunday),
                updated_at: new FormControl(leave.updated_at),
                interval: new FormControl((leave.interval_week > 0))
            })
        );
        if (leave.id) {
            this.form()?.controls.common.disable();
        }

        this.subscriptions.add(this.form()?.controls.common.valueChanges.subscribe(common => {
            if (common) {
                this.form()?.controls.users.disable();
                this.form()?.controls.users.reset();
            } else {
                this.form()?.controls.users.enable();
            }
        }));
        if (leave.id) {
            this.subscriptions.add(
                this.leaveService.getLeaveRealisations(leave.id).subscribe(realistations => {
                    this.registrations.set(realistations);
                })
            );
        }
        this.subscriptions.add(
            this.form()?.controls.interval.valueChanges.subscribe(interval => {
                if (this.form()?.value.interval !== interval) {
                    this.form()?.patchValue({
                        repeat_monday: interval,
                        repeat_tuesday: interval,
                        repeat_wednesday: interval,
                        repeat_thursday: interval,
                        repeat_friday: interval
                    });
                    if (interval) {
                        this.form()?.controls.interval_week.setValue(1);
                    } else {
                        this.form()?.controls.interval_week.setValue(0);
                    }
                }
            })
        );

        this.subscriptions.add(
            this.form()?.controls.begindate.valueChanges.subscribe(begindate => {
                if (!this.form()?.controls.interval.value) {
                    if (begindate.getHours() !== 0) {
                        const beginDate = new Date(begindate);
                        Utils.setTime(beginDate, 0, 0, 0);
                        this.form()?.controls.begindate.setValue(beginDate);
                    }
                }
                if ((this.form()?.value.enddate?.getTime() < begindate.getTime()) || !this.form()?.controls.interval.value) {
                    const endDate = new Date(begindate);
                    Utils.setTime(endDate, 23, 59, 59);
                    this.form()?.controls.enddate.setValue(endDate);
                }

                this.maxDate = new Date(begindate);
                if (this.form()?.getRawValue().common) {
                    this.maxDate.setDate(this.maxDate.getDate() + 28);
                } else {
                    this.maxDate.setFullYear(this.maxDate.getFullYear() + 3);
                }
            })
        );

        this.subscriptions.add(
            this.form()?.valueChanges.pipe(debounceTime(500), startWith()).subscribe(() => {
                this.duration = Utils.minuteDuration(this.form()?.value.endtime, this.form()?.value.begintime);
                this.generateRealisations();
            })
        );

        if (this.leave().id !== undefined) {
            const datetimeBefore = new DatetimeBeforePipe();
            if ((!datetimeBefore.transform(this.now, new Date(this.leave().enddate))) || (!AuthorisationService.hasRole(Roles.Management) && leave.approved_by && leave.approved_at)) {
                this.form().disable();
                this.canSaveLeave = false;
                this.duration = Utils.minuteDuration(this.form()?.controls.endtime.value, this.form()?.controls.begintime.value);

            }
        }
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form()?.valid && this.canSaveLeave) {
            const leave = new Leave();
            Object.assign(leave, this.form()?.value);
            leave.begintime = formatDate(leave.begintime, 'HH:mm', 'nl');
            leave.endtime = formatDate(leave.endtime, 'HH:mm', 'nl');

            this.subscriptions.add(
                this.leaveService.saveLeave(leave).subscribe(() => {
                    this.router.navigate([Routenames.leave]);
                })
            );
        }
    }

    mouseOverDate(date: Date) {
        this.highlight = formatDate(date, 'yyyy-MM-dd', 'nl');
    }

    mouseOverUser(userId: number) {
        this.highlight = userId;
    }

    generateRealisations() {
        let formBegindate: string | number | Date;
        let formEnddate: string | number | Date;
        let formBegintime: Date;
        let formEndtime: Date;
        let formInterval: boolean;
        let formIntervalWeek: number;

        if (!this.canSaveLeave) {
            formBegindate = this.form()?.controls.begindate.value;
            formEnddate = this.form()?.controls.enddate.value;
            formBegintime = this.form()?.controls.begintime.value;
            formEndtime = this.form()?.controls.endtime.value;
            formInterval = this.form()?.controls.interval.value;
            formIntervalWeek = this.form()?.controls.interval_week.value;
        } else {
            formBegindate = this.form()?.value.begindate;
            formEnddate = this.form()?.value.enddate;
            formBegintime = this.form()?.value.begintime;
            formEndtime = this.form()?.value.endtime;
            formInterval = this.form()?.value.interval;
            formIntervalWeek = this.form()?.value.interval_week;
        }

        const realisations = [];
        const loopDate = new Date(formBegindate);
        const lastEndDate = new Date(formEnddate);
        const firstWeek = +formatDate(loopDate, 'w', 'nl');
        const startTime = new Date(formBegindate);
        Utils.setTime(startTime, formBegintime.getHours(), formBegintime.getMinutes());
        const endTime = new Date(formEnddate);
        Utils.setTime(endTime, formEndtime.getHours(), formEndtime.getMinutes());

        const loopEndDate = new Date(formEnddate);
        Utils.setTime(loopEndDate, 23, 59, 59);

        while (loopDate.getTime() < loopEndDate.getTime()) {

            let generateThisDay = !formInterval;
            if (formIntervalWeek > 0) {
                const week = +formatDate(loopDate, 'w', 'nl');
                const thisWeek = ((week - firstWeek) % formIntervalWeek) === 0;
                const weekDayName = formatDate(loopDate, 'EEEE', 'en').toLowerCase();
                const thisDay = this.form()?.value[`repeat_${weekDayName}`];
                generateThisDay = thisWeek && thisDay;
            }

            if (generateThisDay) {
                const beginDate = new Date(loopDate);
                if (beginDate.getTime() === Utils.getTimeOrNull(formBegindate) && beginDate.getTime() > startTime.getTime()) {
                    Utils.setTime(beginDate, formBegindate.getHours(), formBegindate.getMinutes(), 0);
                } else {
                    Utils.setTime(beginDate, startTime.getHours(), startTime.getMinutes(), 0);
                }

                const endDate = new Date(loopDate);
                Utils.setTime(endDate, formEnddate.getHours(), formEnddate.getMinutes(), 0);

                if (endDate.getTime() !== lastEndDate.getTime() || lastEndDate.getTime() > endTime.getTime()) {
                    Utils.setTime(endDate, endTime.getHours(), endTime.getMinutes(), 0);
                }

                const realisation = new Registration();
                realisation.from = beginDate;
                realisation.till = endDate;
                realisations.push(realisation);
            }
            loopDate.setDate(loopDate.getDate() + 1);
        }
        this.previewRealisations.set(realisations);
    }
}

export interface FormGroupLeave extends Omit<Leave, 'begindate' | 'enddate' | 'updated_at' | 'users' | 'begintime' | 'endtime' | 'leave_type_class'> {
    begindate: FormControl<Date>;
    enddate: FormControl<Date>;
    begintime: FormControl<Date>;
    endtime: FormControl<Date>;
    interval: boolean;
    users: FormControl<number[]>;
    updated_at: FormControl<Date>;
}
